import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faHotel } from '@fortawesome/pro-regular-svg-icons/faHotel';
import { faHouse } from '@fortawesome/pro-regular-svg-icons/faHouse';
import { faHouseBuilding } from '@fortawesome/pro-regular-svg-icons/faHouseBuilding';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ServiceLocation } from '@spike/appointments-list-action';
import clsx from 'clsx';
import { MobileDrawerItem } from 'components/UI/V2/MobileDrawer/Item/MobileDrawerItem';
import { MobileDrawer } from 'components/UI/V2/MobileDrawer/MobileDrawer';
import Button from 'components/UI/V3/Button/Button';
import { FunctionComponent, useState } from 'react';
import ClickAwayListener from 'react-click-away-listener';
import { useMediaQuery } from 'react-responsive';
import filterStyles from './Filter.module.scss';
import styles from './ServiceTypeFilter.module.scss';

interface ServiceTypeOption {
    id: ServiceLocation | undefined;
    name: string;
    icon: IconDefinition;
}

// No id means no service_location filter, the unfiltered list.
const options: Array<ServiceTypeOption> = [
    { id: undefined, name: 'All Service Types', icon: faHouseBuilding },
    { id: 'at_facility', name: 'At Facility', icon: faHotel },
    { id: 'in_home', name: 'Home Service', icon: faHouse }
];

interface ServiceTypeFilterProps {
    selected?: ServiceLocation;
    className?: string;
    onChange?: (selected: ServiceLocation | undefined) => void;
}

export const ServiceTypeFilter: FunctionComponent<ServiceTypeFilterProps> = props => {
    const isMobile = useMediaQuery({ query: '(max-width:600px)' });

    const [expanded, setExpanded] = useState(false);

    const selectedOption = options.find(option => option.id === props.selected) ?? options[0];

    const selectHandler = (option: ServiceTypeOption) => {
        setExpanded(false);
        props.onChange?.(option.id);
    };

    return (
        <div className={clsx(filterStyles.container, props.className)}>
            <Button
                variant="outlined"
                label={selectedOption.name}
                leftIcon={selectedOption.icon}
                size="large"
                onClick={() => setExpanded(prev => !prev)}
                className={filterStyles.button}
            />
            <div
                className={filterStyles.mobileButton}
                onClick={() => setExpanded(prev => !prev)}
            >
                <FontAwesomeIcon icon={selectedOption.icon} />
            </div>
            {expanded && isMobile && (
                <MobileDrawer onClose={() => setExpanded(false)}>
                    {options.map(option => (
                        <MobileDrawerItem
                            key={option.id ?? 'all'}
                            icon={option.icon}
                            label={option.name}
                            onClick={() => selectHandler(option)}
                        />
                    ))}
                </MobileDrawer>
            )}
            {expanded && !isMobile && (
                <ClickAwayListener onClickAway={() => setExpanded(false)}>
                    <ul className={styles.menu}>
                        {options.map(option => (
                            <li key={option.id ?? 'all'}>
                                <button
                                    type="button"
                                    className={styles.option}
                                    onClick={() => selectHandler(option)}
                                >
                                    <FontAwesomeIcon
                                        icon={option.icon}
                                        className={styles.icon}
                                    />
                                    {option.name}
                                </button>
                            </li>
                        ))}
                    </ul>
                </ClickAwayListener>
            )}
        </div>
    );
};

export default ServiceTypeFilter;
